/**
 * Gates load-more: shows a collection banner first; `<load-more>` is mounted only
 * after activation. All products on the current page remain visible (no row hiding).
 * @extends HTMLElement
 */
class McCollectionViewAllGate extends HTMLElement {
  /** @type {AbortController | null} */
  #resizeAbort = null;

  /** @type {(() => void) | null} */
  #facetUnsubscribe = null;

  /** @type {boolean} */
  #released = false;

  connectedCallback() {
    if (this.dataset.mcGateInit === '1') return;
    this.dataset.mcGateInit = '1';

    const root = this.closest('[data-mc-view-all-section]');
    const grid = root?.querySelector('[data-product-grid]');
    if (!root || !grid) return;

    this.#resizeAbort = new AbortController();
    const scheduleGapFix = () => McCollectionViewAllGate.fixPhantomGap(grid, this);
    scheduleGapFix();
    requestAnimationFrame(scheduleGapFix);
    setTimeout(scheduleGapFix, 200);
    window.addEventListener('resize', scheduleGapFix, { signal: this.#resizeAbort.signal });

    this.addEventListener('click', this.#onActivate);
    this.addEventListener('keydown', this.#onKeydown);

    if (this.dataset.totalPages) {
      window.McLoadMorePrefetch?.startPrefetch?.(2);
      this.#facetUnsubscribe = subscribe(PUB_SUB_EVENTS.facetsUpdate, () => {
        if (!this.dataset.totalPages || this.#released) return;
        window.McLoadMorePrefetch?.clear?.();
        window.McLoadMorePrefetch?.startPrefetch?.(2);
      });
    }
  }

  disconnectedCallback() {
    this.#resizeAbort?.abort();
    this.#resizeAbort = null;
    this.#facetUnsubscribe?.();
    this.#facetUnsubscribe = null;
    this.removeEventListener('click', this.#onActivate);
    this.removeEventListener('keydown', this.#onKeydown);
  }

  /**
   * @param {KeyboardEvent} e
   */
  #onKeydown = (e) => {
    if (this.#released || e.repeat) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.#release();
    }
  };

  /**
   * @param {MouseEvent} e
   */
  #onActivate = (e) => {
    if (this.#released) return;
    e.preventDefault();
    e.stopPropagation();
    this.#release();
  };

  #release() {
    if (this.#released) return;
    this.#released = true;

    const root = this.closest('[data-mc-view-all-section]');
    const grid = root?.querySelector('[data-product-grid]');
    if (!root || !grid) {
      this.remove();
      return;
    }

    const prevAnchorBody = document.body.style.overflowAnchor;
    const prevAnchorHtml = document.documentElement.style.overflowAnchor;
    document.body.style.overflowAnchor = 'none';
    document.documentElement.style.overflowAnchor = 'none';

    this.style.setProperty('--mc-banner-margin-top', '0');

    const totalPages = this.dataset.totalPages;
    if (totalPages) {
      McCollectionViewAllGate.mountLoadMore(this, totalPages, this.dataset.totalItems);
    }

    this.#resizeAbort?.abort();
    this.#resizeAbort = null;
    this.remove();

    setTimeout(() => {
      document.body.style.overflowAnchor = prevAnchorBody || '';
      document.documentElement.style.overflowAnchor = prevAnchorHtml || '';
    }, 500);
  }

  /**
   * @param {HTMLElement} grid
   * @param {HTMLElement} banner
   */
  static fixPhantomGap(grid, banner) {
    const items = grid.querySelectorAll('li[data-product-item]');
    if (items.length === 0) return;

    const lastVisible = items[items.length - 1];
    const gridRect = grid.getBoundingClientRect();
    const lastRect = lastVisible.getBoundingClientRect();
    const phantomGap = gridRect.bottom - lastRect.bottom;

    const gridStyle = getComputedStyle(grid);
    const rowGap = parseFloat(gridStyle.rowGap) || parseFloat(gridStyle.gap) || 0;

    const pullUp = phantomGap - rowGap;
    if (pullUp > 1) {
      banner.style.setProperty('--mc-banner-margin-top', '-' + pullUp + 'px');
    } else if (pullUp < -1) {
      banner.style.setProperty('--mc-banner-margin-top', rowGap - phantomGap + 'px');
    } else {
      banner.style.setProperty('--mc-banner-margin-top', '0');
    }
  }

  /**
   * Inserts `<load-more>` matching `snippets/load-more.liquid`, then removes the gate node.
   * @param {HTMLElement} gateEl Host element (insertion reference).
   * @param {string} totalPages
   * @param {string} [totalItems]
   */
  static mountLoadMore(gateEl, totalPages, totalItems) {
    const loadMore = document.createElement('load-more');
    loadMore.className = 'load-more-data empty:tw-block';
    loadMore.setAttribute('data-total-pages', totalPages);
    if (totalItems) {
      loadMore.setAttribute('data-total-items', totalItems);
    }

    const parent = gateEl.parentNode;
    if (parent) {
      parent.insertBefore(loadMore, gateEl);
    }
  }
}

if (!customElements.get('mc-collection-view-all-gate')) {
  customElements.define('mc-collection-view-all-gate', McCollectionViewAllGate);
}
